"use client";

import { useAuth } from "@/context/AuthContext";
import { db, storage } from "@/lib/firebase";
import { updateProfile } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, Building, Camera, Loader2, Phone, Save, User, X } from "lucide-react";
import { useEffect, useState } from "react";

interface ProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function ProfileModal({ isOpen, onClose }: ProfileModalProps) {
    const { user, refreshUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [formData, setFormData] = useState({
        displayName: "",
        photoURL: "",
        companyName: "",
        phoneNumber: ""
    });

    useEffect(() => {
        if (isOpen && user) {
            setFormData(prev => ({
                ...prev,
                displayName: user.displayName || "",
                photoURL: user.photoURL || ""
            }));
            fetchUserData();
        }
    }, [isOpen, user]);

    const fetchUserData = async () => {
        if (!user) return;
        setFetching(true);
        try {
            const docRef = doc(db, "users", user.uid);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                setFormData(prev => ({
                    ...prev,
                    companyName: data.companyName || "",
                    phoneNumber: data.phoneNumber || ""
                }));
            }
        } catch (err) {
            console.error("Error fetching user data:", err);
        } finally {
            setFetching(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setSuccess("");
        setLoading(true);

        if (!user) return;

        try {
            // Update Auth Profile
            await updateProfile(user, {
                displayName: formData.displayName,
                photoURL: formData.photoURL
            });

            // Update Firestore Profile
            await setDoc(doc(db, "users", user.uid), {
                companyName: formData.companyName,
                phoneNumber: formData.phoneNumber,
                updatedAt: new Date(),
                email: user.email // Keep email in sync for reference
            }, { merge: true });

            await refreshUser();

            setSuccess("Profile updated successfully!");
            setTimeout(() => {
                onClose();
                setSuccess("");
            }, 1000); // Close after success message
        } catch (err: any) {
            console.error("Error updating profile:", err);
            setError(err.message || "Failed to update profile.");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                {/* Backdrop */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                />

                {/* Modal */}
                <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 20 }}
                    className="relative bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden"
                >
                    {/* Header */}
                    <div className="px-8 pt-8 pb-4 border-b border-gray-100">
                        <div className="flex justify-between items-center">
                            <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600">
                                Edit Profile
                            </h2>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="h-5 w-5 text-gray-500" />
                            </button>
                        </div>
                    </div>

                    {/* Form */}
                    {fetching ? (
                        <div className="p-12 flex flex-col items-center justify-center text-gray-400">
                            <Loader2 className="h-8 w-8 animate-spin mb-2" />
                            <p>Loading profile...</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-6">
                            {error && (
                                <div className="text-sm p-3 rounded-xl bg-red-50 text-red-600 flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4 shrink-0" />
                                    {error}
                                </div>
                            )}
                            {success && (
                                <div className="text-sm p-3 rounded-xl bg-green-50 text-green-600 flex items-center gap-2">
                                    <Save className="h-4 w-4 shrink-0" />
                                    {success}
                                </div>
                            )}

                            {/* Profile Photo */}
                            <div className="flex flex-col items-center gap-4">
                                <div className="relative h-24 w-24 rounded-full bg-gray-100 overflow-hidden border border-gray-200 shrink-0 group hover:border-black transition-colors cursor-pointer shadow-sm">
                                    <label htmlFor="photo-upload" className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity z-10 cursor-pointer">
                                        <Camera className="h-8 w-8 text-white" />
                                    </label>
                                    <input
                                        type="file"
                                        id="photo-upload"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            if (!file || !user) return;

                                            try {
                                                setLoading(true);
                                                const storageRef = ref(storage, `users/${user.uid}/profile_photo`);
                                                const snapshot = await uploadBytes(storageRef, file);
                                                const downloadURL = await getDownloadURL(snapshot.ref);

                                                await updateProfile(user, { photoURL: downloadURL });
                                                await refreshUser();

                                                setFormData(prev => ({ ...prev, photoURL: downloadURL }));
                                            } catch (err) {
                                                console.error("Error uploading photo:", err);
                                                setError("Failed to upload photo.");
                                            } finally {
                                                setLoading(false);
                                            }
                                        }}
                                    />
                                    {formData.photoURL ? (
                                        <img src={formData.photoURL} alt="Profile" className="h-full w-full object-cover" />
                                    ) : (
                                        <div className="h-full w-full flex items-center justify-center bg-gray-900 text-white font-bold text-3xl">
                                            {formData.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "U"}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-4">
                                {/* Display Name */}
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700">Full Name</label>
                                    <div className="relative group">
                                        <User className="absolute left-3 top-3.5 h-4 w-4 text-gray-400 group-focus-within:text-black transition-colors" />
                                        <input
                                            type="text"
                                            placeholder="John Doe"
                                            value={formData.displayName}
                                            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                                            data-gramm="false"
                                            className="w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 focus:border-black focus:ring-0 outline-none transition-all"
                                        />
                                    </div>
                                </div>

                                {/* Company Name */}
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700">Company Name</label>
                                    <div className="relative group">
                                        <Building className="absolute left-3 top-3.5 h-4 w-4 text-gray-400 group-focus-within:text-black transition-colors" />
                                        <input
                                            type="text"
                                            placeholder="Acme Inc."
                                            value={formData.companyName}
                                            onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                                            data-gramm="false"
                                            className="w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 focus:border-black focus:ring-0 outline-none transition-all"
                                        />
                                    </div>
                                </div>

                                {/* Phone Number */}
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700">Phone Number</label>
                                    <div className="relative group">
                                        <Phone className="absolute left-3 top-3.5 h-4 w-4 text-gray-400 group-focus-within:text-black transition-colors" />
                                        <input
                                            type="tel"
                                            placeholder="+1 (555) 000-0000"
                                            value={formData.phoneNumber}
                                            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                                            data-gramm="false"
                                            className="w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 focus:border-black focus:ring-0 outline-none transition-all"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="pt-2">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-black text-white font-bold py-3.5 rounded-xl hover:bg-gray-800 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0"
                                >
                                    {loading ? (
                                        <Loader2 className="h-5 w-5 animate-spin" />
                                    ) : (
                                        "Save Changes"
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="w-full mt-3 py-3 text-sm text-gray-500 hover:text-black transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
